import axios from 'axios';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

export const api = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (res) => res.data,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

export interface SearchResponse {
  total: number;
  items: any[];
}

export interface TimelineResponse {
  total: number;
  items: any[];
}

// Добавь в существующий файл api.ts

export interface SearchFilters {
  type?: string;
  authority?: string;
  status?: string;
  // Новые фильтры
  region_code?: string;
  is_federal?: boolean;
  doc_state?: string;
  date_from?: string;
  date_to?: string;
  keywords?: string;
  doc_number?: string;
}

export async function searchDocs(
  q: string,
  filters: SearchFilters,
  from: number = 0,
  size: number = 20,
  sort: string = 'relevance'
) {
  const params = new URLSearchParams();
  params.append('q', q);
  params.append('from', String(from));
  params.append('size', String(size));
  params.append('sort', sort);
  
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== '' && value !== null) {
      params.append(key, String(value));
    }
  });

  const res = await fetch(`/api/search?${params}`);
  if (!res.ok) throw new Error(`Search failed: ${res.status}`);
  return res.json();
}

// Справочники для фильтров
export async function getRegions() {
  const res = await fetch('/api/search/regions');
  if (!res.ok) throw new Error('Failed to load regions');
  return res.json();
}

export async function getDocTypes() {
  const res = await fetch('/api/search/types');
  if (!res.ok) throw new Error('Failed to load types');
  return res.json();
}

export async function getAuthorities() {
  const res = await fetch('/api/search/authorities');
  if (!res.ok) throw new Error('Failed to load authorities');
  return res.json();
}

export async function getSearchStats() {
  const res = await fetch('/api/search/stats');
  if (!res.ok) throw new Error('Failed to load stats');
  return res.json();
}



export const getDoc = (id: string): Promise<any> => api.get(`/documents/${id}`);
export const getDocByEo = (eo: string): Promise<any> => api.get(`/documents/eo/${eo}`);
export const getRelated = (id: string): Promise<any> => api.get(`/documents/${id}/related`);
export const getHistory = (id: string): Promise<any> => api.get(`/documents/${id}/history`);
export const getTimeline = (from = 0, size = 50): Promise<TimelineResponse> => api.get('/timeline', { params: { from, size } });

export const getStats = (): Promise<any> => api.get('/stats');

export const getSegments = (): Promise<any> => api.get('/segments');
export const createSegment = (data: any): Promise<any> => api.post('/segments', data);
export const deleteSegment = (id: string): Promise<any> => api.delete(`/segments/${id}`);

export const toggleFavorite = (docId: string): Promise<any> => api.post(`/favorites/${docId}`);
export const getFavorites = (): Promise<any> => api.get('/favorites');

export const saveNote = (docId: string, content: string): Promise<any> => api.put(`/notes/${docId}`, { content });
export const getNote = (docId: string): Promise<any> => api.get(`/notes/${docId}`);
export const getNotes = (): Promise<any> => api.get('/notes');

export const login = (email: string, password: string): Promise<any> =>
  api.post('/auth/login', { email, password });
export const register = (email: string, password: string, name: string): Promise<any> =>
  api.post('/auth/register', { email, password, name });
export const getMe = (): Promise<any> => api.get('/auth/me');

export const getIngestionLog = (limit = 50): Promise<any> => api.get('/admin/ingestion-log', { params: { limit } });
// ===== ACTUAL.PRAVO.GOV.RU API =====

export interface ActualDocument {
  docid: string;
  docname: string;
  docpassing: string;
  docstate: string;
  adoption_date: string;
  adoption_number: string;
  actual_redid: string;
  linked_document_id: string | null;
  linked_eo: string | null;
  linked_title: string | null;
}

export interface ActualArticle {
  id: number;
  article_type: string;
  article_number: string;
  article_title: string;
  article_text: string;
  children?: ActualArticle[];
}

export const getActualDocuments = (page = 1, limit = 20, search = ''): Promise<{total: number, items: ActualDocument[]}> => 
  api.get('/actual/documents', { params: { page, limit, search } });

export const getActualDocument = (docid: string): Promise<{document: ActualDocument, redactions: any[]}> => 
  api.get(`/actual/documents/${docid}`);

export const getActualArticles = (redid: string): Promise<{redid: string, articles: ActualArticle[]}> => 
  api.get(`/actual/redactions/${redid}/content`);

export const getActualHtml = (redid: string): Promise<{redid: string, html: string}> => 
  api.get(`/actual/redactions/${redid}/html`);

export const ingestActual = (data: {docid?: number, law_number?: string, hash?: string}): Promise<any> => 
  api.post('/actual/ingest', data);

export const askActualRag = (question: string, docid?: number): Promise<any> => 
  api.post('/actual/rag/ask', { question, docid });

// ====== Document Links & Gohash Resolution ======

export interface ActualDocumentLink {
  target_docid: string;
  target_docname: string;
  target_docpassing: string;
  target_number: string;
  link_type: string;
  confidence: number;
}

export const getActualDocumentLinks = (docid: string): Promise<{docid: string, links: ActualDocumentLink[]}> => 
  api.get(`/actual/documents/${docid}/links`);

export const resolveGohash = (gohash: string): Promise<{gohash: string, docid: string, docname?: string, source: string}> => 
  api.get(`/actual/gohash/${gohash}`);

// ===== TOPICS API =====

export interface Topic {
  code: string;
  name: string;
  level: number;
  keywords: string[];
  doc_count: number;
}

export interface TopicStats {
  coverage: { covered: number; total: number; coverage_pct: number };
  by_method: { match_type: string; cnt: number }[];
  top_topics: { code: string; name: string; doc_count: number }[];
}

export const getTopics = (): Promise<{ topics: Topic[] }> => 
  api.get('/actual/topics');

export const getTopicStats = (): Promise<TopicStats> => 
  api.get('/actual/topics/stats');

export const getTopicDocuments = (code: string, page = 1, limit = 20): Promise<{total: number, page: number, items: any[]}> => 
  api.get(`/actual/topics/${code}/documents`, { params: { page, limit } });

export const getTopicTimeline = (): Promise<{ timeline: any[] }> => 
  api.get('/actual/topics/timeline');

export const getDocumentTopics = (docid: string): Promise<{ topics: any[] }> =>
  api.get(`/actual/documents/${docid}/topics`);
